/**
 * API client functions for the Project Service.
 * Uses the shared apiClient with credentials: include and base URL from env.
 */

import { apiClient } from '@/modules/api/api-client';

import type {
    ArchiveProjectResponse,
    CreateProjectRequest,
    ProjectActivityEntry,
    ProjectDetail,
    ProjectListResponse,
    ProjectPr,
    ProjectPrsResponse,
    ProjectRepository,
    UpdateProjectRequest
} from '@/types/project';

/**
 * Lists all active projects visible to the current user.
 * Projects are sorted by most recent activity descending.
 */
export async function listProjects(): Promise<ProjectListResponse> {
  return apiClient.get<ProjectListResponse>('/projects');
}

/**
 * Gets a single project by key, including linked repositories.
 */
export async function getProject(key: string): Promise<ProjectDetail> {
  return apiClient.get<ProjectDetail>(`/projects/${encodeURIComponent(key)}`);
}

/**
 * Creates a new project. Requires admin role.
 */
export async function createProject(req: CreateProjectRequest): Promise<ProjectDetail> {
  return apiClient.post<ProjectDetail>('/projects', req);
}

/**
 * Updates an existing project's display name and/or inventory.
 * The project key cannot be changed. Requires admin role.
 */
export async function updateProject(key: string, req: UpdateProjectRequest): Promise<ProjectDetail> {
  return apiClient.patch<ProjectDetail>(`/projects/${encodeURIComponent(key)}`, req);
}

/**
 * Archives a project. Requires admin role.
 * Archived projects are excluded from the default project list.
 */
export async function archiveProject(key: string): Promise<ArchiveProjectResponse> {
  return apiClient.post<ArchiveProjectResponse>(`/projects/${encodeURIComponent(key)}/archive`);
}

/**
 * Lists repositories linked to a project by prefix matching.
 */
export async function listProjectRepos(key: string): Promise<ProjectRepository[]> {
  return apiClient.get<ProjectRepository[]>(`/projects/${encodeURIComponent(key)}/repos`);
}

/**
 * Gets recent activity entries across all repositories in a project.
 * Returns the most recent 20 entries ordered by timestamp descending.
 */
export async function getProjectActivity(key: string): Promise<ProjectActivityEntry[]> {
  return apiClient.get<ProjectActivityEntry[]>(`/projects/${encodeURIComponent(key)}/activity`);
}

/**
 * Lists all OPEN pull requests across the project's repositories, newest first.
 * Served by the PR Manager service under /prs/* to avoid the Project Service proxy route.
 * Merged and closed PRs are excluded.
 */
export async function listProjectOpenPrs(key: string): Promise<ProjectPr[]> {
  const response = await apiClient.get<ProjectPrsResponse>(
    `/prs/project/${encodeURIComponent(key)}`,
  );
  return response.items;
}

/**
 * Lists all archived projects. Requires admin role.
 */
export async function listArchivedProjects(): Promise<ProjectListResponse> {
  return apiClient.get<ProjectListResponse>('/projects/archived');
}
